import { Info, TriangleAlert } from 'lucide-react'
import type { ReactNode } from 'react'

import { cn } from '@/lib/cn'

export interface BannerProps {
  tone?: 'attention' | 'info' | 'negative'
  /** The lead phrase, coloured. States what is wrong. */
  title: ReactNode
  /** What it prevents, in that order. */
  children?: ReactNode
  action?: ReactNode
  className?: string
}

/** A persistent record-level message. It is NOT a validation message that
 *  appears on submit: the checklist banner (FR-12) lists what is missing from
 *  the moment it is missing, and states what it blocks, so the eventual refusal
 *  is never a surprise. */
export function Banner({
  tone = 'attention',
  title,
  children,
  action,
  className,
}: BannerProps) {
  const Icon = tone === 'info' ? Info : TriangleAlert

  return (
    <div
      className={cn(
        'flex items-start gap-3 rounded-md border px-3.5 py-3',
        tone === 'attention'
          ? 'bg-attention-soft border-attention/25'
          : tone === 'negative'
            ? 'bg-negative-soft border-negative/25'
            : 'bg-brand-soft border-brand-border',
        className,
      )}
    >
      <Icon
        size={16}
        aria-hidden
        className={cn(
          'mt-px shrink-0',
          tone === 'attention'
            ? 'text-attention'
            : tone === 'negative'
              ? 'text-negative'
              : 'text-brand',
        )}
      />
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <p className="text-text-heading text-[13px] leading-snug font-normal">
          <span
            className={cn(
              'font-medium',
              tone === 'attention'
                ? 'text-attention'
                : tone === 'negative'
                  ? 'text-negative'
                  : 'text-brand',
            )}
          >
            {title}
          </span>
        </p>
        {children ? (
          <div className="text-text-body text-[12.5px] leading-snug font-light">
            {children}
          </div>
        ) : null}
      </div>
      {action ? <div className="shrink-0">{action}</div> : null}
    </div>
  )
}
